import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';
import { useSidebar } from 'contexts/SidebarContext';
import {
  CreditCard, Star, AlertTriangle, Calendar, Receipt, TrendingUp,
  Clock, XCircle, RefreshCw, Download, Loader2, ChevronLeft, ChevronRight,
  Info, CheckCircle, Inbox, Menu
} from 'lucide-react';
import {
  getBillingSummary,
  getBillingHistory,
  getCurrentBilling,
  triggerPDFDownload,
  cancelSubscription,
  reactivateSubscription
} from 'services/billing/billingService';
import { getSubscriptions, SubscriptionPlan } from 'services/subscriptions/subscriptionService';

// Look up a Stripe price ID for a plan by name + billing cycle.
// Source of truth is /api/subscription/get-subscriptions (set in DB by admins) — not env.
// Returns '' when the plan or the matching cycle has no price configured.
const findStripePriceId = (
  plans: SubscriptionPlan[],
  planName: string | null | undefined,
  billingCycle: string | null | undefined
): string => {
  if (!planName) return '';
  const plan = plans.find(p => p.subscription_name?.toLowerCase() === planName.toLowerCase());
  if (!plan?.stripe_price_ids) return '';
  return (billingCycle === 'annual' ? plan.stripe_price_ids.annual : plan.stripe_price_ids.monthly) || '';
};

// Define interfaces
type LocalizedText = string | { en: string; fr: string };

interface CurrentSubscription {
  name: string;
  description: LocalizedText;
  status: string;
  isCancelled: boolean;
  isSuspended: boolean;
  isCancelPending: boolean;
  isActive: boolean;
  price: number;
  currency: string;
  startDate: string;
  endDate: string | null;
  nextBillingDate: string | null;
  autoRenew: boolean;
}

interface CancellationInfo {
  cancelledAt: string;
  subscriptionEndDate: string;
  remainingDays: number;
  canReactivate: boolean;
  reactivationBenefit: string;
  previousPlan: {
    name: string;
    monthlyPrice: number;
  };
}

interface PaymentStats {
  totalSpent: number;
  successfulPayments: number;
  averagePayment: number;
  lastPaymentDate: string | null;
  lastPaymentAmount: number;
}

interface UpcomingCharges {
  date: string;
  amount: number;
  description: LocalizedText;
}

interface BillingSummary {
  hasActiveSubscription?: boolean;
  currentSubscription: CurrentSubscription;
  cancellationInfo?: CancellationInfo;
  paymentStats: PaymentStats;
  upcomingCharges: UpcomingCharges | null;
}

interface Payment {
  _id: string;
  invoiceNumber: string;
  subscriptionName: string;
  amount: number;
  status: string;
  paymentMethod: string;
  paymentDate: string;
  subscriptionStartDate: string;
  subscriptionEndDate: string;
  transactionId: string;
  stripeSubscriptionId: string;
}

interface BillingHistory {
  payments: Payment[];
  pagination: {
    currentPage: number;
    itemsPerPage: number;
    totalItems: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

interface CurrentBillingDetails {
  subscription: {
    id: string;
    name: string;
    description: LocalizedText;
    monthlyPrice: number;
    annualPrice: number;
    currency: string;
    numberOfAccounts: number;
    socialAccounts: string[];
  } | null;
  billing: {
    currentPeriodStart: string | null;
    currentPeriodEnd: string | null;
    daysRemaining: number;
    isExpired: boolean;
    nextBillingDate: string | null;
    nextBillingAmount: number;
    autoRenew: boolean;
    lastPaymentDate: string | null;
    lastPaymentAmount: number;
    paymentMethod: string;
    billingCycle?: string;
    billingCycleLabel?: string;
  };
  stripeInfo: {
    customerId: string;
    subscriptionId: string;
    subscriptionStatus: string;
  } | null;
}

const Billing: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';
  const getLocalizedText = (text: LocalizedText | undefined): string => {
    if (!text) return '';
    if (typeof text === 'string') return text;
    return text[lang] || text.en || '';
  };
  const { toggleSidebar } = useSidebar();
  const [loading, setLoading] = useState<boolean>(true);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingHistory, setBillingHistory] = useState<BillingHistory | null>(null);
  const [currentBillingDetails, setCurrentBillingDetails] = useState<CurrentBillingDetails | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [downloadingInvoice, setDownloadingInvoice] = useState<string | null>(null);
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [isCancelling, setIsCancelling] = useState<boolean>(false);
  const [showReactivateModal, setShowReactivateModal] = useState<boolean>(false);
  const [isReactivating, setIsReactivating] = useState<boolean>(false);

  // Theme tokens
  const containerBg = isDark ? '#1D293D' : '#F8FAFC';
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const mainShadow = isDark ? '0px 1px 2px rgba(2, 6, 24, 0.5)' : '0px 1px 2px rgba(43, 127, 255, 0.1)';
  const headerBorder = isDark ? '#1D293D' : '#F1F5F9';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const subtitleColor = isDark ? '#90A1B9' : '#62748E';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const labelColor = isDark ? '#90A1B9' : '#62748E';
  const valueColor = isDark ? '#E2E8F0' : '#314158';
  const tableBorder = isDark ? '#1D293D' : '#F1F5F9';
  const tableHeaderBg = isDark ? '#1D293D' : '#F8FAFC';
  const tableRowHover = isDark ? '#162456' : '#F8FAFC';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const warningBg = isDark ? '#422006' : '#FFFBEB';
  const warningBorder = isDark ? '#92400E' : '#FDE68A';
  const warningText = isDark ? '#FCD34D' : '#92400E';
  const dangerBg = isDark ? '#3B0A0A' : '#FEF2F2';
  const dangerBorder = isDark ? '#7F1D1D' : '#FECACA';
  const dangerText = isDark ? '#FF637E' : '#DC2626';
  const successBg = isDark ? '#022C22' : '#ECFDF5';
  const successBorder = isDark ? '#009966' : '#5EE9B5';
  const successText = isDark ? '#5EE9B5' : '#009966';
  const orangeBg = isDark ? '#431407' : '#FFF7ED';
  const orangeBorder = isDark ? '#9A3412' : '#FED7AA';
  const orangeText = isDark ? '#FB923C' : '#C2410C';
  const buttonBg = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const paginationBg = isDark ? '#1D293D' : '#FFFFFF';
  const paginationBorder = isDark ? '#314158' : '#E2E8F0';
  const paginationText = isDark ? '#CAD5E2' : '#45556C';
  const paginationDisabled = isDark ? '#314158' : '#90A1B9';
  const modalOverlay = isDark ? 'rgba(2, 6, 24, 0.7)' : 'rgba(0, 0, 0, 0.5)';
  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#314158' : '#E2E8F0';
  const linkColor = isDark ? '#51A2FF' : '#2B7FFF';

  useEffect(() => {
    fetchBillingData();
    fetchBillingHistory(1);
  }, []);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      const [summary, currentDetails, plansResp] = await Promise.all([
        getBillingSummary(),
        getCurrentBilling().catch(() => null),
        getSubscriptions().catch(() => null)
      ]);
      setBillingSummary(summary);
      setCurrentBillingDetails(currentDetails);
      if (plansResp?.plans) setPlans(plansResp.plans);
    } catch (error: any) {
      console.error('Billing fetch error:', error);
      if (error.message.includes('No authentication token')) {
        toast.error(t('billing.errors.sessionExpired') || 'Session expired');
        navigate('/');
      } else {
        toast.error(error.message || t('billing.errors.failedToLoad') || 'Failed to load billing data');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingHistory = async (page: number) => {
    try {
      const history = await getBillingHistory(page, 10);
      setBillingHistory(history);
    } catch (error: any) {
      console.error('Billing history fetch error:', error);
      toast.error(error.message || t('billing.errors.failedToLoadHistory'));
    }
  };

  const handleDownloadInvoice = async (paymentId: string, invoiceNumber: string) => {
    try {
      setDownloadingInvoice(paymentId);
      await triggerPDFDownload(paymentId, invoiceNumber);
      toast.success(t('billing.invoiceDownloaded') || 'Invoice downloaded successfully');
    } catch (error: any) {
      console.error('Invoice download error:', error);
      toast.error(error.message || t('billing.errors.failedToDownload'));
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      setIsCancelling(true);
      const response = await cancelSubscription();
      toast.success(response.message || t('billing.cancelSuccess'));
      if (response.details) {
        setTimeout(() => {
          toast.success(
            t('billing.cancelDetails', {
              accounts: response.details.socialMediaAccountsDeactivated,
              date: new Date(response.details.cancelledAt).toLocaleDateString()
            }) || `${response.details.socialMediaAccountsDeactivated} accounts deactivated`
          );
        }, 1500);
      }
      setShowCancelModal(false);
      setTimeout(() => { fetchBillingData(); fetchBillingHistory(1); }, 2000);
    } catch (error: any) {
      console.error('Cancel subscription error:', error);
      toast.error(error.message || t('billing.errors.failedToCancel'));
    } finally {
      setIsCancelling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    try {
      if (!billingSummary) return;
      setIsReactivating(true);
      // For cancel-pending reactivation, the backend just flips cancel_at_period_end:false on the
      // existing Stripe subscription — no price ID needed. The fully-cancelled path routes through
      // a fresh Stripe checkout and needs a price ID, which we resolve from the plans API.
      const isReversingCancel = billingSummary.currentSubscription.isCancelPending;
      const planName = isReversingCancel
        ? billingSummary.currentSubscription.name
        : (billingSummary.cancellationInfo?.previousPlan?.name || billingSummary.currentSubscription.name);
      const billingCycle = currentBillingDetails?.billing?.billingCycle || 'monthly';
      const stripePriceId = isReversingCancel ? '' : findStripePriceId(plans, planName, billingCycle);
      if (!isReversingCancel && !stripePriceId && planName && planName.toLowerCase() !== 'trial') {
        toast.error(t('billing.errors.stripePriceIdNotConfigured') || `Stripe price ID not configured for "${planName}".`);
        setIsReactivating(false);
        return;
      }
      toast.loading(t('billing.reactivating') || 'Reactivating subscription...');
      const response = await reactivateSubscription(stripePriceId);
      toast.dismiss();
      if ('url' in response && response.url) {
        toast.success(t('billing.redirectingToCheckout') || 'Redirecting to checkout...');
        setShowReactivateModal(false);
        setTimeout(() => { window.location.href = response.url; }, 1000);
      } else {
        toast.success(response.message || t('billing.reactivateSuccess'));
        if ('details' in response && response.details) {
          const details = response.details as Record<string, any>;
          if (details.remainingDays > 0 && details.nextBillingDate) {
            setTimeout(() => {
              toast.success(
                t('billing.reactivateDetails', {
                  days: details.remainingDays,
                  date: new Date(details.nextBillingDate).toLocaleDateString()
                }) || `You have ${details.remainingDays} days remaining.`
              );
            }, 1500);
          } else if (details.reactivationType === 'cancellation_reversed' && details.subscriptionEndDate) {
            setTimeout(() => {
              toast.success(
                t('billing.cancellationReversed', {
                  date: new Date(details.subscriptionEndDate).toLocaleDateString()
                }) || `Your subscription has been reactivated. Next billing date: ${new Date(details.subscriptionEndDate).toLocaleDateString()}.`
              );
            }, 1500);
          }
        }
        setShowReactivateModal(false);
        setTimeout(() => { fetchBillingData(); fetchBillingHistory(1); }, 2000);
      }
    } catch (error: any) {
      toast.dismiss();
      console.error('Reactivate subscription error:', error);
      toast.error(error.message || t('billing.errors.failedToReactivate'));
    } finally {
      setIsReactivating(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    fetchBillingHistory(newPage);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const getStatusTag = (status: string) => {
    const s = status.toLowerCase();
    let bg: string, border: string, color: string;
    if (s === 'active' || s === 'completed') { bg = successBg; border = successBorder; color = successText; }
    else if (s === 'pending') { bg = warningBg; border = warningBorder; color = warningText; }
    else if (s === 'cancelling' || s === 'cancelled') { bg = orangeBg; border = orangeBorder; color = orangeText; }
    else if (s === 'failed' || s === 'suspended') { bg = dangerBg; border = dangerBorder; color = dangerText; }
    else { bg = cardBg; border = cardBorder; color = subtitleColor; }
    return (
      <span style={{ fontSize: '12px', lineHeight: '16px', fontWeight: 500, background: bg, border: `1px solid ${border}`, color, borderRadius: '999px', padding: '2px 8px', whiteSpace: 'nowrap' as const }}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Helper: key-value row
  const InfoRow = ({ label, value, border = true }: { label: string; value: React.ReactNode; border?: boolean }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: border ? `1px solid ${sectionBorder}` : 'none' }}>
      <span style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 400, color: labelColor }}>{label}</span>
      <span style={{ fontSize: '13px', lineHeight: '18px', fontWeight: 500, color: valueColor }}>{value}</span>
    </div>
  );

  // Helper: section card
  const SectionCard = ({ icon, iconBg, iconColor, title, headerBg, headerBorderColor, children }: {
    icon: React.ReactNode; iconBg: string; iconColor: string; title: string;
    headerBg?: string; headerBorderColor?: string; children: React.ReactNode;
  }) => (
    <div style={{ background: mainBg, border: `1px solid ${headerBorderColor || cardBorder}`, borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderBottom: `1px solid ${headerBorderColor || cardBorder}`, background: headerBg || mainBg }}>
        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: iconColor }}>
          {icon}
        </div>
        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: headerBg ? iconColor : titleColor }}>{title}</span>
      </div>
      <div style={{ padding: '20px' }}>{children}</div>
    </div>
  );

  if (loading) return <Spinner size="large" text={t('common.loading')} fullPage />;

  return (
    <div
      className="flex flex-col h-full p-0 lg:pt-2 lg:pr-2 lg:pb-2 lg:pl-0"
      style={{ gap: '8px', background: containerBg }}
    >
      {/* Main content wrapper */}
      <div
        className="flex flex-col flex-1 w-full overflow-hidden rounded-none lg:rounded-2xl"
        style={{ background: mainBg, boxShadow: mainShadow }}
      >
        {/* Header */}
        <div
          className="flex items-center w-full flex-shrink-0"
          style={{ padding: '12px 16px', borderBottom: `1px solid ${headerBorder}`, background: mainBg }}
        >
          <div className="flex items-center w-full" style={{ gap: '8px' }}>
            <button
              className="lg:hidden flex items-center justify-start flex-shrink-0"
              style={{ width: '24px', height: '36px', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
              onClick={toggleSidebar}
            >
              <Menu size={24} color={isDark ? '#E2E8F0' : '#314158'} />
            </button>
            <div className="hidden lg:flex" style={{ width: '36px', height: '36px', borderRadius: '12px', background: isDark ? '#022C22' : '#ECFDF5', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CreditCard size={24} color={isDark ? '#5EE9B5' : '#009966'} />
            </div>
            <div style={{ flex: 1 }}>
              <h1 style={{ fontSize: '20px', lineHeight: '28px', fontWeight: 700, color: titleColor, margin: 0 }}>
                {t('billing.title')}
              </h1>
            </div>
          </div>
        </div>

        {/* Scrollable content */}
        <div
          className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
          style={{ flex: 1, overflowY: 'auto' }}
        >
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {billingSummary && (
              <>
                {/* 3-column grid: left 2 cols, right 1 col */}
                <div className="grid grid-cols-1 lg:grid-cols-3" style={{ gap: '24px' }}>
                  {/* Left column */}
                  <div className="lg:col-span-2" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                    {/* Current Subscription */}
                    <SectionCard
                      icon={<CreditCard size={16} />}
                      iconBg={isDark ? '#162456' : '#EFF6FF'}
                      iconColor={isDark ? '#51A2FF' : '#2B7FFF'}
                      title={t('billing.currentSubscription')}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {billingSummary.hasActiveSubscription === false ? (
                          <div style={{ padding: '24px 12px', textAlign: 'center', color: subtitleColor }}>
                            <CreditCard size={28} style={{ opacity: 0.4, marginBottom: '10px' }} />
                            <p style={{ fontSize: '14px', fontWeight: 600, color: titleColor, margin: '0 0 4px' }}>
                              {t('billing.empty.noSubscriptionTitle', 'No active subscription')}
                            </p>
                            <p style={{ fontSize: '13px', margin: '0 0 16px' }}>
                              {t('billing.empty.noSubscriptionMessage', 'Choose a plan to start your 7-day free trial. Card required, cancel anytime.')}
                            </p>
                            <button
                              onClick={() => navigate('/onboarding')}
                              style={{
                                padding: '10px 20px', fontSize: '13px', fontWeight: 600,
                                color: '#FFFFFF', background: 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)',
                                border: 'none', borderRadius: '8px', cursor: 'pointer'
                              }}
                            >
                              {t('billing.empty.chooseAPlan', 'Choose a plan')}
                            </button>
                          </div>
                        ) : (
                          <>
                            <InfoRow label={t('billing.plan')} value={billingSummary.currentSubscription.name} />
                            <InfoRow label={t('billing.status')} value={getStatusTag(billingSummary.currentSubscription.status)} />
                            <InfoRow label={t('billing.price')} value={`${formatCurrency(billingSummary.currentSubscription.price, billingSummary.currentSubscription.currency)}/${t('billing.month')}`} />
                            <InfoRow label={t('billing.nextBilling')} value={billingSummary.currentSubscription.nextBillingDate ? formatDate(billingSummary.currentSubscription.nextBillingDate) : 'N/A'} />
                            <InfoRow label={t('billing.startDate')} value={formatDate(billingSummary.currentSubscription.startDate)} />
                            <InfoRow label={t('billing.endDate')} value={billingSummary.currentSubscription.endDate ? formatDate(billingSummary.currentSubscription.endDate) : 'N/A'} border={false} />
                          </>
                        )}

                        {/* Cancel Button — hidden while cancel is already pending */}
                        {billingSummary.currentSubscription.isActive && !billingSummary.currentSubscription.isCancelled && !billingSummary.currentSubscription.isCancelPending && (
                          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={() => setShowCancelModal(true)}
                              style={{
                                width: '100%', padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: dangerText,
                                background: dangerBg, border: `1px solid ${dangerBorder}`, borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                              }}
                            >
                              <XCircle size={14} />
                              {t('billing.cancelSubscription')}
                            </button>
                            <span style={{ fontSize: '12px', color: subtitleColor }}>{t('billing.cancelNote')}</span>
                          </div>
                        )}

                        {/* Reactivate Button — also shown during cancel-pending so user can reverse */}
                        {(billingSummary.currentSubscription.isCancelled || billingSummary.currentSubscription.isSuspended || billingSummary.currentSubscription.isCancelPending) &&
                         billingSummary.cancellationInfo?.canReactivate && (
                          <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                            <button
                              onClick={() => setShowReactivateModal(true)}
                              style={{
                                width: '100%', padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: successText,
                                background: successBg, border: `1px solid ${successBorder}`, borderRadius: '8px', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                              }}
                            >
                              <RefreshCw size={14} />
                              {t('billing.reactivateSubscription')}
                            </button>
                            <span style={{ fontSize: '12px', color: subtitleColor }}>{t('billing.reactivateNote')}</span>
                          </div>
                        )}
                      </div>
                    </SectionCard>

                    {/* Subscription Features — only render when there's a real active subscription */}
                    {currentBillingDetails && currentBillingDetails.subscription && (
                      <SectionCard
                        icon={<Star size={16} />}
                        iconBg={isDark ? '#3C0366' : '#FAF5FF'}
                        iconColor={isDark ? '#C27AFF' : '#AD46FF'}
                        title={t('billing.subscriptionFeatures')}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                          {/* Plan info banner */}
                          <div style={{ padding: '12px', background: infoBg, border: `1px solid ${infoBorder}`, borderRadius: '8px' }}>
                            <span style={{ fontSize: '14px', fontWeight: 600, color: infoText, display: 'block', marginBottom: '4px' }}>
                              {currentBillingDetails.subscription.name}
                            </span>
                            <span style={{ fontSize: '13px', fontWeight: 400, color: infoText }}>
                              {getLocalizedText(currentBillingDetails.subscription.description)}
                            </span>
                          </div>

                          {/* Quick stats */}
                          <div className="grid grid-cols-2" style={{ gap: '12px' }}>
                            <div style={{ padding: '12px', background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '8px' }}>
                              <span style={{ fontSize: '12px', color: labelColor, display: 'block', marginBottom: '4px' }}>{t('billing.socialAccounts')}</span>
                              <span style={{ fontSize: '18px', fontWeight: 700, color: titleColor }}>{currentBillingDetails.subscription.numberOfAccounts}</span>
                            </div>
                            <div style={{ padding: '12px', background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: '8px' }}>
                              <span style={{ fontSize: '12px', color: labelColor, display: 'block', marginBottom: '4px' }}>{t('billing.daysRemaining')}</span>
                              <span style={{ fontSize: '18px', fontWeight: 700, color: titleColor }}>
                                {currentBillingDetails.billing.daysRemaining > 0 ? currentBillingDetails.billing.daysRemaining : 0}
                              </span>
                            </div>
                          </div>

                          {/* Supported platforms */}
                          {currentBillingDetails.subscription.socialAccounts?.length > 0 && (
                            <div>
                              <span style={{ fontSize: '13px', color: labelColor, display: 'block', marginBottom: '8px' }}>{t('billing.supportedPlatforms')}</span>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {currentBillingDetails.subscription.socialAccounts.map((platform, index) => (
                                  <span key={index} style={{ padding: '4px 10px', fontSize: '12px', fontWeight: 500, color: infoText, background: infoBg, border: `1px solid ${infoBorder}`, borderRadius: '6px' }}>
                                    {platform}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Payment method & auto-renew */}
                          <div style={{ borderTop: `1px solid ${sectionBorder}`, paddingTop: '12px' }}>
                            <InfoRow label={t('billing.paymentMethod')} value={<span style={{ textTransform: 'capitalize' as const }}>{currentBillingDetails.billing.paymentMethod || 'N/A'}</span>} />
                            <InfoRow label={t('billing.autoRenew')} value={getStatusTag(
                              billingSummary?.currentSubscription.isCancelPending
                                ? 'Cancelling'
                                : (currentBillingDetails.billing.autoRenew ? 'Active' : 'Pending')
                            )} border={false} />
                          </div>

                          {/* Expired warning */}
                          {currentBillingDetails.billing.isExpired && !billingSummary?.currentSubscription.isActive && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: dangerBg, border: `1px solid ${dangerBorder}`, borderRadius: '8px' }}>
                              <AlertTriangle size={16} style={{ flexShrink: 0 }} color={dangerText} />
                              <span style={{ fontSize: '13px', fontWeight: 500, color: dangerText }}>{t('billing.subscriptionExpired')}</span>
                            </div>
                          )}
                        </div>
                      </SectionCard>
                    )}

                    {/* Cancellation Info — shown for cancel-pending (warning) AND fully cancelled/suspended */}
                    {billingSummary.cancellationInfo && (billingSummary.currentSubscription.isCancelled || billingSummary.currentSubscription.isSuspended || billingSummary.currentSubscription.isCancelPending) && (
                      <SectionCard
                        icon={<AlertTriangle size={16} />}
                        iconBg={orangeBg}
                        iconColor={orangeText}
                        title={t('billing.cancellationInfo')}
                        headerBg={orangeBg}
                        headerBorderColor={orangeBorder}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <InfoRow label={t('billing.cancelledOn')} value={formatDate(billingSummary.cancellationInfo.cancelledAt)} />
                          <InfoRow label={t('billing.accessUntil')} value={formatDate(billingSummary.cancellationInfo.subscriptionEndDate)} />
                          <InfoRow label={t('billing.remainingDays')} value={
                            <span style={{ fontWeight: 600, color: orangeText }}>{billingSummary.cancellationInfo.remainingDays} {t('billing.days')}</span>
                          } border={false} />
                          {billingSummary.cancellationInfo.canReactivate && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: successBg, border: `1px solid ${successBorder}`, borderRadius: '8px', marginTop: '12px' }}>
                              <Info size={16} style={{ flexShrink: 0 }} color={successText} />
                              <span style={{ fontSize: '13px', fontWeight: 400, color: successText }}>{billingSummary.cancellationInfo.reactivationBenefit}</span>
                            </div>
                          )}
                        </div>
                      </SectionCard>
                    )}

                    {/* Upcoming Charges */}
                    {billingSummary.upcomingCharges && (
                      <SectionCard
                        icon={<Calendar size={16} />}
                        iconBg={warningBg}
                        iconColor={warningText}
                        title={t('billing.upcomingCharges')}
                        headerBg={warningBg}
                        headerBorderColor={warningBorder}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div>
                            <span style={{ fontSize: '14px', fontWeight: 500, color: titleColor, display: 'block' }}>{getLocalizedText(billingSummary.upcomingCharges.description)}</span>
                            <span style={{ fontSize: '13px', color: subtitleColor, marginTop: '2px', display: 'block' }}>{formatDate(billingSummary.upcomingCharges.date)}</span>
                          </div>
                          <span style={{ fontSize: '20px', fontWeight: 700, color: warningText }}>{formatCurrency(billingSummary.upcomingCharges.amount)}</span>
                        </div>
                      </SectionCard>
                    )}

                    {/* Payment History */}
                    <div style={{ background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '12px', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', borderBottom: `1px solid ${cardBorder}` }}>
                        <div style={{ width: '28px', height: '28px', borderRadius: '8px', background: isDark ? '#162456' : '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Receipt size={16} color={isDark ? '#51A2FF' : '#2B7FFF'} />
                        </div>
                        <span style={{ fontSize: '14px', lineHeight: '20px', fontWeight: 600, color: titleColor }}>{t('billing.tabs.history')}</span>
                      </div>

                      {billingHistory && billingHistory.payments.length > 0 ? (
                        <>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                              <thead>
                                <tr style={{ background: tableHeaderBg, borderBottom: `1px solid ${tableBorder}` }}>
                                  {[t('billing.invoice'), t('billing.date'), t('billing.amount'), t('billing.status'), t('billing.actions')].map((h, i) => (
                                    <th key={i} style={{ padding: '10px 20px', textAlign: 'left' as const, fontSize: '12px', fontWeight: 500, color: labelColor }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {billingHistory.payments.map((payment) => (
                                  <tr
                                    key={payment._id}
                                    style={{ borderBottom: `1px solid ${tableBorder}` }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = tableRowHover; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                  >
                                    <td style={{ padding: '10px 20px', fontSize: '13px', color: valueColor }}>{payment.invoiceNumber}</td>
                                    <td style={{ padding: '10px 20px', fontSize: '13px', color: labelColor }}>{formatDate(payment.paymentDate)}</td>
                                    <td style={{ padding: '10px 20px', fontSize: '13px', fontWeight: 500, color: valueColor }}>{formatCurrency(payment.amount)}</td>
                                    <td style={{ padding: '10px 20px' }}>{getStatusTag(payment.status)}</td>
                                    <td style={{ padding: '10px 20px' }}>
                                      <button
                                        onClick={() => handleDownloadInvoice(payment._id, payment.invoiceNumber)}
                                        disabled={downloadingInvoice === payment._id}
                                        style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: 500, color: linkColor, background: 'none', border: 'none', cursor: 'pointer', opacity: downloadingInvoice === payment._id ? 0.5 : 1 }}
                                      >
                                        {downloadingInvoice === payment._id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                                        {downloadingInvoice !== payment._id && t('billing.download')}
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* Pagination */}
                          {billingHistory.pagination.totalPages > 1 && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px', padding: '12px 20px', borderTop: `1px solid ${tableBorder}` }}>
                              <span style={{ fontSize: '12px', color: labelColor }}>
                                {t('billing.showing')} {(billingHistory.pagination.currentPage - 1) * billingHistory.pagination.itemsPerPage + 1}-
                                {Math.min(billingHistory.pagination.currentPage * billingHistory.pagination.itemsPerPage, billingHistory.pagination.totalItems)} {t('billing.of')} {billingHistory.pagination.totalItems}
                              </span>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  onClick={() => handlePageChange(currentPage - 1)}
                                  disabled={!billingHistory.pagination.hasPreviousPage}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '12px', fontWeight: 500,
                                    color: billingHistory.pagination.hasPreviousPage ? paginationText : paginationDisabled,
                                    background: paginationBg, border: `1px solid ${paginationBorder}`, borderRadius: '6px',
                                    cursor: billingHistory.pagination.hasPreviousPage ? 'pointer' : 'not-allowed', opacity: billingHistory.pagination.hasPreviousPage ? 1 : 0.5
                                  }}
                                >
                                  <ChevronLeft size={14} /> {t('billing.previous')}
                                </button>
                                <button
                                  onClick={() => handlePageChange(currentPage + 1)}
                                  disabled={!billingHistory.pagination.hasNextPage}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: '4px', padding: '6px 12px', fontSize: '12px', fontWeight: 500,
                                    color: billingHistory.pagination.hasNextPage ? paginationText : paginationDisabled,
                                    background: paginationBg, border: `1px solid ${paginationBorder}`, borderRadius: '6px',
                                    cursor: billingHistory.pagination.hasNextPage ? 'pointer' : 'not-allowed', opacity: billingHistory.pagination.hasNextPage ? 1 : 0.5
                                  }}
                                >
                                  {t('billing.next')} <ChevronRight size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: '8px' }}>
                          <Inbox size={32} color={subtitleColor} />
                          <span style={{ fontSize: '14px', color: subtitleColor }}>{t('billing.noPayments')}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Right column — Stats (only rendered when there's a real subscription history) */}
                  {billingSummary.hasActiveSubscription !== false && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                      {/* Payment Statistics */}
                      <SectionCard
                        icon={<TrendingUp size={16} />}
                        iconBg={isDark ? '#162456' : '#EFF6FF'}
                        iconColor={isDark ? '#51A2FF' : '#2B7FFF'}
                        title={t('billing.paymentStats')}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <InfoRow label={t('billing.totalSpent')} value={
                            <span style={{ fontWeight: 600, color: isDark ? '#51A2FF' : '#2B7FFF' }}>{formatCurrency(billingSummary.paymentStats.totalSpent)}</span>
                          } />
                          <InfoRow label={t('billing.successfulPayments')} value={
                            <span style={{ fontWeight: 600, color: successText }}>{billingSummary.paymentStats.successfulPayments}</span>
                          } />
                          <InfoRow label={t('billing.averagePayment')} value={
                            <span style={{ fontWeight: 600, color: isDark ? '#C27AFF' : '#AD46FF' }}>{formatCurrency(billingSummary.paymentStats.averagePayment)}</span>
                          } border={false} />
                        </div>
                      </SectionCard>

                      {/* Last Payment */}
                      <SectionCard
                        icon={<Clock size={16} />}
                        iconBg={isDark ? '#162456' : '#EFF6FF'}
                        iconColor={isDark ? '#51A2FF' : '#2B7FFF'}
                        title={t('billing.lastPayment')}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <InfoRow label={t('billing.lastPaymentDate')} value={billingSummary.paymentStats.lastPaymentDate ? formatDate(billingSummary.paymentStats.lastPaymentDate) : 'N/A'} />
                          <InfoRow label={t('billing.lastPaymentAmount')} value={billingSummary.paymentStats.lastPaymentDate != null ? formatCurrency(billingSummary.paymentStats.lastPaymentAmount || 0) : 'N/A'} border={false} />
                        </div>
                      </SectionCard>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div style={{ position: 'fixed', inset: 0, background: modalOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: modalBg, border: `1px solid ${modalBorder}`, borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: isDark ? '0 25px 50px rgba(2,6,24,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px', borderBottom: `1px solid ${modalBorder}` }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: dangerBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <AlertTriangle size={20} color={dangerText} />
              </div>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor, display: 'block' }}>{t('billing.cancelModal.title')}</span>
                <span style={{ fontSize: '13px', color: subtitleColor }}>{t('billing.cancelModal.subtitle')}</span>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ padding: '12px', background: dangerBg, border: `1px solid ${dangerBorder}`, borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: dangerText, display: 'block', marginBottom: '8px' }}>{t('billing.cancelModal.warning')}</span>
                <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <li style={{ fontSize: '13px', color: dangerText }}>{t('billing.cancelModal.consequence1')}</li>
                  <li style={{ fontSize: '13px', color: dangerText }}>{t('billing.cancelModal.consequence2')}</li>
                  <li style={{ fontSize: '13px', color: dangerText }}>{t('billing.cancelModal.consequence3')}</li>
                </ul>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: infoBg, border: `1px solid ${infoBorder}`, borderRadius: '8px' }}>
                <Info size={16} style={{ flexShrink: 0 }} color={infoText} />
                <span style={{ fontSize: '13px', color: infoText }}>{t('billing.cancelModal.accessNote')}</span>
              </div>
              <span style={{ fontSize: '13px', color: subtitleColor }}>{t('billing.cancelModal.confirmation')}</span>
            </div>
            {/* Footer */}
            <div style={{ display: 'flex', gap: '12px', padding: '16px 24px', borderTop: `1px solid ${modalBorder}` }}>
              <button
                onClick={() => setShowCancelModal(false)}
                disabled={isCancelling}
                style={{ flex: 1, padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: titleColor, background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', cursor: 'pointer', opacity: isCancelling ? 0.5 : 1 }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                style={{ flex: 1, padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#FFFFFF', background: isDark ? '#DC2626' : '#DC2626', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: isCancelling ? 0.5 : 1 }}
              >
                {isCancelling ? <><Loader2 size={14} className="animate-spin" /> {t('billing.cancelModal.cancelling')}</> : <><XCircle size={14} /> {t('billing.cancelModal.confirmButton')}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivate Subscription Modal */}
      {showReactivateModal && (
        <div style={{ position: 'fixed', inset: 0, background: modalOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '16px' }}>
          <div style={{ background: modalBg, border: `1px solid ${modalBorder}`, borderRadius: '16px', maxWidth: '480px', width: '100%', boxShadow: isDark ? '0 25px 50px rgba(2,6,24,0.5)' : '0 25px 50px rgba(0,0,0,0.15)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '20px 24px', borderBottom: `1px solid ${modalBorder}` }}>
              <div style={{ width: '36px', height: '36px', borderRadius: '12px', background: successBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <RefreshCw size={20} color={successText} />
              </div>
              <div>
                <span style={{ fontSize: '16px', fontWeight: 600, color: titleColor, display: 'block' }}>{t('billing.reactivateModal.title')}</span>
                <span style={{ fontSize: '13px', color: subtitleColor }}>{t('billing.reactivateModal.subtitle')}</span>
              </div>
            </div>
            {/* Body */}
            <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ padding: '12px', background: successBg, border: `1px solid ${successBorder}`, borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 500, color: successText, display: 'block', marginBottom: '8px' }}>{t('billing.reactivateModal.benefits')}</span>
                <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <li style={{ fontSize: '13px', color: successText }}>{t('billing.reactivateModal.benefit1')}</li>
                  <li style={{ fontSize: '13px', color: successText }}>{t('billing.reactivateModal.benefit2')}</li>
                  <li style={{ fontSize: '13px', color: successText }}>{t('billing.reactivateModal.benefit3')}</li>
                </ul>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px', background: infoBg, border: `1px solid ${infoBorder}`, borderRadius: '8px' }}>
                <Info size={16} style={{ flexShrink: 0 }} color={infoText} />
                <span style={{ fontSize: '13px', color: infoText }}>{t('billing.reactivateModal.remainingDaysNote')}</span>
              </div>
              <span style={{ fontSize: '13px', color: subtitleColor }}>{t('billing.reactivateModal.confirmation')}</span>
            </div>
            {/* Footer */}
            <div style={{ display: 'flex', gap: '12px', padding: '16px 24px', borderTop: `1px solid ${modalBorder}` }}>
              <button
                onClick={() => setShowReactivateModal(false)}
                disabled={isReactivating}
                style={{ flex: 1, padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: titleColor, background: mainBg, border: `1px solid ${cardBorder}`, borderRadius: '8px', cursor: 'pointer', opacity: isReactivating ? 0.5 : 1 }}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleReactivateSubscription}
                disabled={isReactivating}
                style={{ flex: 1, padding: '8px 16px', fontSize: '13px', fontWeight: 500, color: '#FFFFFF', background: isDark ? '#009966' : '#009966', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', opacity: isReactivating ? 0.5 : 1 }}
              >
                {isReactivating ? <><Loader2 size={14} className="animate-spin" /> {t('billing.reactivateModal.reactivating')}</> : <><RefreshCw size={14} /> {t('billing.reactivateModal.confirmButton')}</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Billing;
